import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import './AccountSettings.css';

const AccountSettings: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Matomo settings state
  const [matomoEmbedCode, setMatomoEmbedCode] = useState('');
  const [enableMatomo, setEnableMatomo] = useState(false);
  const [matomoLoading, setMatomoLoading] = useState(false);
  const [matomoError, setMatomoError] = useState('');
  const [matomoSuccess, setMatomoSuccess] = useState('');

  // Fetch Matomo configuration on component mount
  useEffect(() => {
    fetchMatomoConfig();
  }, []);

  const fetchMatomoConfig = async () => {
    try {
      const response = await fetch('/api/matomo/config');
      if (response.ok) {
        const config = await response.json();
        if (config.embedCode) setMatomoEmbedCode(config.embedCode);
        setEnableMatomo(config.enableAnalytics || false);
      }
    } catch (err) {
      console.error('Failed to fetch Matomo config:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Az új jelszavak nem egyeznek');
      return;
    }

    if (newPassword.length < 6) {
      setError('Az új jelszónak legalább 6 karakter hosszúnak kell lennie');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (response.ok) {
        setSuccess('A jelszó sikeresen megváltozott');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'A jelszó megváltoztatása sikertelen');
      }
    } catch (err) {
      setError('Hálózati hiba történt');
    } finally {
      setLoading(false);
    }
  };

  const handleMatomoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMatomoError('');
    setMatomoSuccess('');

    if (!matomoEmbedCode) {
      setMatomoError('A Matomo embed kód megadása kötelező');
      return;
    }

    setMatomoLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/matomo/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          embedCode: matomoEmbedCode,
          enableAnalytics: enableMatomo
        })
      });

      if (response.ok) {
        setMatomoSuccess('A Matomo beállítások sikeresen mentve');
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        const errorData = await response.json();
        setMatomoError(errorData.error || 'A Matomo beállítások mentése sikertelen');
      }
    } catch (err) {
      setMatomoError('Hálózati hiba történt');
    } finally {
      setMatomoLoading(false);
    }
  };

  return (
    <AdminLayout title="Fiók beállítások">
      <div className="account-settings-content">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="settings-section">
          <h2>Jelszó megváltoztatása</h2>
          <p>Itt megváltoztathatja a fiókja jelszavát</p>
          
          <form onSubmit={handleSubmit} className="password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Jelenlegi jelszó</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Adja meg a jelenlegi jelszavát"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Új jelszó</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Adja meg az új jelszavát"
                required
                disabled={loading}
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Új jelszó megerősítése</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Erősítse meg az új jelszavát"
                required
                disabled={loading}
                minLength={6}
              />
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="save-btn" 
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
              >
                {loading ? 'Mentés...' : 'Jelszó mentése'}
              </button>
            </div>
          </form>

          <div className="info-box">
            <h3>Jelszó követelmények:</h3>
            <ul>
              <li>Legalább 6 karakter hosszúságú</li>
              <li>Tartalmazzon betűket és számokat</li>
              <li>Kerülje a gyakran használt jelszavakat</li>
            </ul>
          </div>
        </div>

        <div className="settings-section">
          <h2>Matomo Analytics Beállítások</h2>
          <p>Itt konfigurálhatja a Matomo analytics szolgáltatást a weboldal látogatottságának méréséhez</p>
          
          {matomoError && <div className="error-message">{matomoError}</div>}
          {matomoSuccess && <div className="success-message">{matomoSuccess}</div>}
          
          <form onSubmit={handleMatomoSubmit} className="matomo-form">
            <div className="form-group">
              <label htmlFor="enableMatomo">
                <input
                  type="checkbox"
                  id="enableMatomo"
                  checked={enableMatomo}
                  onChange={(e) => setEnableMatomo(e.target.checked)}
                />
                Analytics engedélyezése
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="matomoEmbedCode">Matomo Embed Kód</label>
              <textarea
                id="matomoEmbedCode"
                value={matomoEmbedCode}
                onChange={(e) => setMatomoEmbedCode(e.target.value)}
                placeholder="Illessze be a Matomo által biztosított JavaScript kódot ide..."
                disabled={matomoLoading}
                rows={8}
                style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '12px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="save-btn" 
                disabled={matomoLoading || (!enableMatomo && !matomoEmbedCode)}
              >
                {matomoLoading ? 'Mentés...' : 'Matomo beállítások mentése'}
              </button>
            </div>
          </form>

          <div className="info-box">
            <h3>Matomo beállítások útmutató:</h3>
            <ul>
              <li>A Matomo admin felületén keresse meg a "Tracking Code" vagy "Embed Code" opciót</li>
              <li>Másolja ki a teljes JavaScript kódot (általában &lt;script&gt; tagek között)</li>
              <li>Pasztilja be a fenti mezőbe a teljes kódot</li>
              <li>Az analytics csak akkor aktív, ha az engedélyezés be van jelölve</li>
              <li>A beállítások mentése után az analytics azonnal aktív lesz</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AccountSettings;
